/**
 * The browser-side network client (the GO/NO-GO milestone).
 *
 * This is the counterpart to {@link GameServer}: it makes the browser a *thin
 * client* of the authoritative server. The browser no longer owns a {@link World}
 * it mutates - it holds a **read-model** `World` reconciled from the server's
 * snapshot stream, and it expresses player intent the one sanctioned way, by
 * sending the *existing* sim {@link Command} union up the socket as a
 * {@link ClientCommandMessage}. Every rule still lives in `sim/`; the client just
 * renders what the server says and forwards what the player wants.
 *
 * Deliberately transport-agnostic and free of DOM, Pixi, and any wall clock: the
 * constructor takes a plain `send` callback and the only inbound entry point is
 * {@link receive}(rawData). `main.ts` wires a browser `WebSocket` to it; a test
 * wires a Node `ws` socket to it. That keeps the read-model/command logic unit
 * testable in plain Node, and keeps the simulation/presentation split intact.
 *
 * The read-model is the {@link ClientReplica} (ADR-0324): a full snapshot or
 * keyframe constructs/replaces it through the persistence-backed
 * `snapshotToWorld` helper - one serialization story, so it is equivalent to the
 * server's world at that tick - while an ordinary field-level delta mutates it
 * **incrementally** (only added/removed/cleared/changed state; unchanged
 * entities and components retain object identity), which the existing
 * {@link GameRenderer} consumes unchanged.
 *
 * Local-player movement is **predicted** (UO-style per-step): the
 * {@link PredictedWalker} moves the local character instantly and sends one
 * {@link WalkRequestMessage} per turn/step through {@link sendWalk}; the server
 * answers each off-snapshot via {@link onMoveAck}/{@link onMoveReject}. Every
 * *other* character still renders from the authoritative snapshot stream.
 */

import type { Command, EntityId, StarterRecipeId, World } from '../sim/index';
import type { HeroStats } from '../sim/hero';
import { ClientReplica } from './clientReplica';
import type { ClientPerformanceMonitor } from '../performance/clientPerformance';
import {
  decode,
  encode,
  type ClientId,
  type ClientMessage,
  type ProtocolMessage,
  type LoginMessage,
  type WorldSnapshotMessage,
  type ActionAnimationMessage,
  type RatSwarmStatusMessage,
  type StormStatusMessage,
  type StormTelegraphMessage,
  type DungeonTelegraphMessage,
  type JusticeFxMessage,
  type BloodFxMessage,
  type SpeechMessage,
  type ChatHistoryRemoveMessage,
  type WardrobePair,
  type WardrobeFavoritesStateMessage,
  type CardStateMessage,
  type WireDeckCard,
  type WireCardLink,
  type WireNamedDeck,
  type WalkRequestMessage,
  type DungeonMoveIntentMessage,
  type MoveRejectMessage,
  type DuelPromptMessage,
  type DuelCombatMessage,
  type SpherePromptMessage,
  type SphereStateMessage,
  type SphereEventMessage,
  type SphereCommandResultMessage,
  type DiceWagerPromptMessage,
  type CardDuelPromptMessage,
  type CardDuelListingsMessage,
  type CardDuelListingAcceptedMessage,
  type CardDuelStateMessage,
  type CombatTextMessage,
  type DungeonRunStatusMessage,
  type DungeonCastResultMessage,
  type SoulstoneRosterMessage,
  type ArenaRunStatusMessage,
  type ArenaDraftMessage,
  type ArenaResultsMessage,
  type ArenaRecordMessage,
  type ArenaFxMessage,
  type ArenaWarningMessage,
  type ArenaHistoryPageMessage,
  type ProfileState,
  type AdminSize,
  type WireEarnedAchievement,
} from './protocol';

/**
 * Transport sink: hand a wire-encoded message to the underlying socket. The client
 * only ever sends the JSON message types, all of which {@link encode} returns as a
 * `string` - the binary path (ADR-0190) is server->client only - so the sink stays
 * `string`; the client casts {@link encode}'s widened `string | Uint8Array` return at
 * each of its (always-JSON) send sites via {@link sendJson}.
 */
export type Send = (data: string) => void;

export class GameClient {
  /**
   * The client-owned incremental replica (ADR-0324): a full `Snapshot` or
   * `Keyframe` constructs/replaces its read-model {@link World} wholesale
   * (the authoritative recovery path), while an ordinary `Delta` mutates only
   * the added/removed/cleared/changed state in place - unchanged entities and
   * components retain object identity, so per-broadcast allocation scales with
   * what changed, not with world size.
   */
  private readonly replica = new ClientReplica();
  private performanceMonitor?: ClientPerformanceMonitor;
  /** The EntityId the server assigned this client's player (learned from Welcome). */
  private ownedEntity?: EntityId;
  /** The opaque client id the server assigned (echoed back on every command). */
  private assignedClientId?: ClientId;

  /**
   * Fired once, when the {@link WelcomeMessage} arrives, with the owned player
   * {@link EntityId} and the seeded read-model. `main.ts` uses it to construct
   * the renderer (which needs the map carried by that first snapshot) and to
   * point the camera at the local player.
   */
  onWelcome?: (playerId: EntityId, world: World) => void;

  /**
   * Fired on **every** {@link WelcomeMessage} (so once per reconnect), with the
   * server's baked build id and its force-reload flag (Issue #320). `main.ts`
   * compares the id to the client's own baked `VITE_BUILD_ID`: a mismatch means
   * the server has been patched since this page loaded, so it offers a "new version
   * available" reload prompt (or reloads immediately when `forceReload` is set).
   * Fired **before** the snapshot is adopted so a compatibility-breaking deploy that
   * makes the snapshot un-deserializable still routes the stale client into the
   * reload flow rather than crashing (see {@link onVersionSkew}). A no-op when
   * unwired, and inert when either side's build id is empty.
   */
  onServerBuild?: (serverBuildId: string | undefined, forceReload: boolean) => void;

  /** Fired on every Welcome with the server-authoritative card-game launch gate. */
  onCardsEnabled?: (enabled: boolean) => void;

  /**
   * Fired on **every** {@link WelcomeMessage} (so once per reconnect) with whether this
   * connection is a passcode-proven admin (ADR-0183). `main.ts` uses it **only** to
   * reveal the admin UI (wire the F10 panel, arm the teleport key) - it grants no
   * power, since the server re-authorizes every admin command against the socket's own
   * identity. Fired with `false` for a non-admin/anonymous session (or when the field
   * is absent, e.g. an older server), so a reconnect that loses admin re-hides the UI.
   * A no-op when unwired.
   */
  onIsAdmin?: (isAdmin: boolean) => void;
  onProfile?: (profile: ProfileState) => void;
  onAchievementAward?: (achievement: WireEarnedAchievement) => void;

  /**
   * Fired when adopting a server snapshot **throws** - the load-bearing case being a
   * `SCHEMA_VERSION` skew after a breaking deploy, where `deserialize` throws
   * `Incompatible save version` (Issue #320). Historically that throw escaped the
   * socket `message` listener, `onWelcome` never fired, and the player was stranded
   * under "Reconnecting…" forever. {@link receive} now catches it and hands it here
   * so `main.ts` can degrade to the reload flow (the stale client cannot read this
   * world, so it must refresh onto the new bundle). A no-op when unwired.
   */
  onVersionSkew?: (error: unknown) => void;

  /**
   * Fired on every snapshot (including the one inside the Welcome), with the
   * freshly rebuilt read-model. `main.ts` feeds it to the renderer so each
   * authoritative tick is reflected on screen.
   */
  onSnapshot?: (world: World) => void;

  /**
   * Signals presentation work independently of the render ticker.
   * Replica traffic requests ambient cadence, while ephemeral visual messages
   * request active cadence for their first frame.
   */
  onPresentationInvalidated?: (active: boolean) => void;
  private keyframePresentationSignature?: string;

  /**
   * Fired on every inbound {@link SpeechMessage} (Slice 5), with the speaker's
   * stamped identity and the spoken line. `main.ts` uses it to float fading
   * overhead text in the speaker's hue and, for player speech, append a journal
   * line (ambient NPC barks float overhead but stay out of the journal - see
   * {@link SpeechMessage.npc}, ADR-0075). Speech is an
   * ephemeral event delivered off the snapshot stream (see ADR-0010), so it has
   * its own callback parallel to {@link onSnapshot} rather than riding the world.
   */
  onSpeech?: (speech: SpeechMessage) => void;

  /** Fired when moderation retracts one author's retained journal lines. */
  onChatHistoryRemove?: (message: ChatHistoryRemoveMessage) => void;

  /**
   * Fired on every inbound {@link ActionAnimationMessage} (Rat Swarm Slice B,
   * ADR-0116): a body-clip broadcast - the stomper's attack swing, the
   * rat's death clip. `main.ts` routes it to
   * {@link GameRenderer.playActionAnimation}, which plays the clip on the
   * target pooled sprite and returns it to standing - once, or the message's
   * optional `repeat` count for a looping gesture (the dance emote, ADR-0387). Like {@link onSpeech} it is
   * an ephemeral event delivered off the snapshot stream, so it has its own
   * callback and never touches the read-model world.
   */
  onActionAnimation?: (msg: ActionAnimationMessage) => void;

  /**
   * Fired on every inbound {@link RatSwarmStatusMessage} (Rat Swarm Slice D,
   * ADR-0119): the live event board - active flag, rats remaining, time
   * remaining, and the kill tallies - pushed whenever it changes (start, each
   * kill, the end) and once at a mid-event login. Slice F's event overlay
   * renders from this callback alone; until then it may go unwired (an
   * undefined callback is a safe no-op, like every other push here). Ephemeral
   * event state delivered off the snapshot stream, so it never touches the
   * read-model world.
   */
  onRatSwarmStatus?: (msg: RatSwarmStatusMessage) => void;

  /**
   * Fired on every inbound {@link StormStatusMessage} (Blackthorn's Storm Slice D,
   * ADR-0151): the live storm board - active flag, combat-clock ticks remaining,
   * living vortex count, living/fallen tallies, and the death feed - pushed whenever it
   * changes (Blackthorn's arrival, each summon, each fall, the end) and once at a
   * mid-storm login. Slice F's event overlay renders from this callback; until then it
   * may go unwired (an undefined callback is a safe no-op). Ephemeral event state off
   * the snapshot stream, so it never touches the read-model world.
   */
  onStormStatus?: (msg: StormStatusMessage) => void;

  /**
   * Fired on every inbound {@link StormTelegraphMessage} (Blackthorn's Storm Slice D,
   * ADR-0151): a one-shot warning that a vortex will materialize on `(x, y)` in
   * `ticksUntil` ticks. Slice E renders the danger marker; until then it goes unwired
   * (a safe no-op). Ephemeral like {@link onActionAnimation}, never world state.
   */
  onStormTelegraph?: (msg: StormTelegraphMessage) => void;

  /**
   * Fired on every inbound {@link JusticeFxMessage} (The King's Justice, PRD #361 /
   * ADR-0194): a one-shot smoke poof at `(x, y, z)` - the client renders the baked
   * `poof` effect strip there and plays the teleport-poof sound proximity-scaled.
   * An undefined callback is a safe no-op. Ephemeral like {@link onStormTelegraph},
   * never world state.
   */
  onJusticeFx?: (msg: JusticeFxMessage) => void;

  /**
   * Fired on every inbound {@link BloodFxMessage} (Blood splatter, PRD #372,
   * Slice B): a one-shot server-rolled scatter of blood decals around a victim
   * tile. The client sprays them through {@link ../render/bloodLayer.BloodLayer.splat}
   * (the same primitive the dev hook and the arena cascade drive). An undefined
   * callback is a safe no-op. Ephemeral like {@link onJusticeFx}, never world state.
   */
  onBloodFx?: (msg: BloodFxMessage) => void;

  /**
   * Fired on every inbound {@link ClearCorpsesMessage}: wipe every fallen-body sprite the
   * renderer is holding on the ground. Broadcast by the server at storm end and from the
   * admin "Clear all bodies" control (the corpse/ghost split, ADR-0140). Slice wires it to
   * {@link GameRenderer.clearAllCorpses}; an undefined callback is a safe no-op. Ephemeral
   * like {@link onStormTelegraph}, never world state.
   */
  onClearCorpses?: () => void;

  /**
   * Fired on every owner-only {@link GoldUpdateMessage} (Rat Swarm Slice D,
   * ADR-0119): this connection's gold balance after a change - the durable
   * account balance for a reserved session (`durable: true`), or the
   * non-persisting this-event gold for an anonymous one (`durable: false`).
   * Slice F's gold HUD renders from these anchors; per-connection account
   * state off the snapshot stream, exactly like {@link onOnlineTime}.
   */
  onGoldUpdate?: (gold: number, durable: boolean) => void;

  /**
   * Fired on every owner-only {@link DuelPromptMessage} (Player duels, PRD #252,
   * ADR-0130): open (`open: true`) the accept/decline window naming the challenger
   * and stake, or dismiss it (`open: false`, on withdraw/expire/resolve). `main.ts`
   * drives the duel prompt UI from this. Ephemeral like {@link onSpeech}: a
   * challenge is an event delivered off the snapshot stream, never world state.
   */
  onDuelPrompt?: (msg: DuelPromptMessage) => void;

  /**
   * Fired on every owner-only {@link DuelCombatMessage} (Fire your bow!, ADR-0255):
   * names this player's live duel opponent when combat opens, so `main.ts` can drive
   * the "Press F to auto-fire your bow!" prompt and target a fired shot. `main.ts` clears
   * its stored opponent when the local player's combat stance drops (any duel end).
   * Ephemeral like {@link onDuelPrompt}: a duel is an event off the snapshot stream.
   */
  onDuelCombat?: (msg: DuelCombatMessage) => void;

  /** Owner-only Sphere Duel challenge state. */
  onSpherePrompt?: (msg: SpherePromptMessage) => void;

  /** The complete, deliberately asymmetric Sphere Duel state visible to this seat. */
  onSphereState?: (msg: SphereStateMessage) => void;

  /** A private audiovisual event from this seat's Sphere Duel instance. */
  onSphereEvent?: (msg: SphereEventMessage) => void;
  /** Authoritative correlation for an immediately presented local command. */
  onSphereCommandResult?: (msg: SphereCommandResultMessage) => void;

  /**
   * Fired on every owner-only {@link DiceWagerPromptMessage} (Dice Wager, ADR-0154): the
   * dice-wager twin of {@link onDuelPrompt} - open (`open: true`) the accept/decline
   * window naming the challenger and stake, or dismiss it (`open: false`). `main.ts`
   * drives the same duel prompt UI from this, with dice copy. Ephemeral like
   * {@link onDuelPrompt}: a challenge is an event off the snapshot stream, never world state.
   */
  onDiceWagerPrompt?: (msg: DiceWagerPromptMessage) => void;

  /**
   * Fired on every owner-only {@link CardDuelPromptMessage} (trading card game, PRD #493
   * Slice 5, ADR-0286): the card-duel twin of {@link onDuelPrompt} - open/dismiss the
   * accept/decline window naming the challenger and stake, with card-duel copy. Ephemeral
   * like {@link onDuelPrompt}: a challenge is an event off the snapshot stream.
   */
  onCardDuelPrompt?: (msg: CardDuelPromptMessage) => void;
  /** Current public looking-for-duel board (including this viewer's ownership marker). */
  onCardDuelListings?: (msg: CardDuelListingsMessage) => void;
  /** Owner-only, exactly-once presentation cue for a successfully claimed listing. */
  onCardDuelListingAccepted?: (msg: CardDuelListingAcceptedMessage) => void;

  /**
   * Fired on every owner-only, per-seat directed {@link CardDuelStateMessage} (trading
   * card game, PRD #493 Slice 5): the whole live card-duel battle state as this client
   * sees it, pushed on every state change (a play, an attack, a turn change, the end). The
   * battle gump renders directly from it. Ephemeral event state off the snapshot stream.
   */
  onCardDuelState?: (msg: CardDuelStateMessage) => void;

  /**
   * Fired on every owner-only {@link ArenaRunStatusMessage} (Arena Slice D, PRD #334 /
   * ADR-0176): the live state of this connection's own Arena run - health, level/XP,
   * wave, kills, clock, build - pushed on entry, on change, on a cadence, and one final
   * `active: false` at run end. Slice E's run HUD renders from this callback; until
   * then it goes unwired (an undefined callback is a safe no-op). Ephemeral event
   * state off the snapshot stream, never the read-model world.
   */
  onArenaRunStatus?: (msg: ArenaRunStatusMessage) => void;

  /**
   * Fired on every owner-only {@link ArenaDraftMessage} (Arena Slice D): the pending
   * level-up draft to pick from while the run holds paused. Slice E's draft overlay
   * renders it and answers with a `PickArenaOption` command; until then it goes
   * unwired (a safe no-op). Ephemeral like {@link onDuelPrompt}.
   */
  onArenaDraft?: (msg: ArenaDraftMessage) => void;

  /**
   * Fired on the one owner-only {@link ArenaResultsMessage} a finished run sends
   * (Arena Slice D): time survived, kills, gold banked, score. Slice E's results
   * screen renders it; until then it goes unwired (a safe no-op). Ephemeral.
   */
  onArenaResults?: (msg: ArenaResultsMessage) => void;

  /**
   * Fired on the broadcast {@link ArenaRecordMessage} (ADR-0210): someone set a
   * new all-time Arena high score. Reaches every client (town and arena), the cue
   * for the one-shot victory sting; the bank-herald text arrives separately on the
   * town-only herald path. Ephemeral, never world state.
   */
  onArenaRecord?: (msg: ArenaRecordMessage) => void;

  /**
   * Fired on every owner-only {@link ArenaFxMessage} (fire-mage rework,
   * ADR-0179): one skill volley's whole FX cascade - projectile flights, chain
   * hops, explosions, flame strikes - plus the per-hit damage numbers (skill
   * hits send no CombatText). The FX renderer animates it; until wired it is a
   * safe no-op. Ephemeral like {@link onActionAnimation}.
   */
  onArenaFx?: (msg: ArenaFxMessage) => void;

  /**
   * Fired on every owner-only {@link ArenaWarningMessage}: an elite or boss just
   * entered the run - `main.ts` fires the entrance FX (warning banner + screen
   * shake + audio sting) off the server's beat-derived `warning` kind, never a
   * body-id guess (progressive elites reuse the boss body, so only the beat can
   * tell them apart). Ephemeral like {@link onActionAnimation}: a dropped frame
   * only misses one entrance sting.
   */
  onArenaWarning?: (msg: ArenaWarningMessage) => void;
  onArenaHistoryPage?: (msg: ArenaHistoryPageMessage) => void;

  /**
   * Fired on every inbound {@link CombatTextMessage} (Player duels, PRD #252,
   * ADR-0131): a landed hit's damage number over a struck duelist (a miss floats
   * nothing). `main.ts` drives the floating-combat-text layer from a hit and the
   * shared whiff sound from a miss. Ephemeral like {@link onActionAnimation}: a
   * swing is an event off the snapshot stream, never world state.
   */
  onCombatText?: (msg: CombatTextMessage) => void;
  onDungeonRunStatus?: (msg: DungeonRunStatusMessage) => void;

  /**
   * Fired on every inbound {@link DungeonTelegraphMessage} (PRD #793): one
   * announced area, carrying the frozen geometry a telegraphed monster ability
   * committed to. The client draws the exact footprint for the declared warning.
   * An undefined callback is a safe no-op. Ephemeral like
   * {@link onStormTelegraph}, never world state.
   */
  onDungeonTelegraph?: (msg: DungeonTelegraphMessage) => void;
  onDungeonCastResult?: (msg: DungeonCastResultMessage) => void;
  onSoulstoneRoster?: (msg: SoulstoneRosterMessage) => void;

  /**
   * Fired when the server **refuses** this login (Lightweight Accounts Slice A),
   * with the human-readable reason. No `Welcome` follows and the socket closes, so
   * `main.ts` uses this to re-show the splash with the message and let the visitor
   * edit the name and retry. The Slice A case is a reserved-name collision; this is
   * the one path where a connection never becomes a participant (see ADR-0038).
   */
  onRejected?: (reason: string) => void;
  /** Fired when the authenticated login needs the one-time character appearance step. */
  onCharacterRequired?: () => void;

  /**
   * Fired on every owner-only {@link OnlineTimeMessage} (Cosmetic Progression
   * Slice D), with this connection's authoritative `totalOnlineMs`. `main.ts` feeds
   * it to the bottom HUD as a fresh server *anchor*: the HUD snaps the XP bar /
   * countdown / own-time to it and extrapolates forward between anchors. Like
   * {@link onSpeech} it is account state delivered off the snapshot stream, so it
   * has its own callback rather than riding the read-model world.
   */
  onOnlineTime?: (totalOnlineMs: number) => void;

  /**
   * Fired on every inbound {@link CollectionMessage} (Cosmetic Progression Slice E,
   * ADR-0050), with the character's full owned `{item, hue}` collection. `main.ts`
   * feeds it to the wardrobe panel so its layer -> item -> hue grouping reflects what
   * the player may wear. Like {@link onSpeech} it is an out-of-snapshot push (the
   * collection is account state, not world state), so it has its own callback.
   */
  onCollection?: (unlocked: WardrobePair[]) => void;
  onWardrobeFavoritesState?: (state: WardrobeFavoritesStateMessage) => void;

  /**
   * Fired on every inbound {@link CardStateMessage} (trading card game, PRD #493
   * Slice 3), with this connection's whole Card Binder state - owned collection,
   * saved decks, unopened pack count, and whether the name is reserved. `main.ts`
   * feeds it to the Card Binder gump. Off-snapshot account state (like {@link
   * onCollection}), pushed in reply to {@link GameClient.openBinder} and after a
   * {@link GameClient.saveCardDecks}.
   */
  onCardState?: (msg: CardStateMessage) => void;

  /**
   * Fired on every inbound {@link import('./protocol').PackOpenedMessage} (trading
   * card game, PRD #493 Slice 4): the five cards a just-opened pack revealed, in
   * flip order. `main.ts` feeds it to the pack-opening flip modal. Off-snapshot,
   * owner-only, pushed right before the {@link onCardState} that banks the cards.
   */
  onPackOpened?: (cards: WireDeckCard[], duplicateGold: number) => void;

  /**
   * Fired on the login {@link import('./protocol').AutoEquipStateMessage} (auto-equip
   * toggle, ADR-0123), with this connection's persisted auto-equip preference.
   * `main.ts` feeds it to the wardrobe so its top-of-panel checkbox opens reflecting
   * the account's saved choice. Off-snapshot account state, so it has its own
   * callback like {@link onCollection}.
   */
  onAutoEquipState?: (enabled: boolean) => void;

  /**
   * Fired on every {@link MoveAckMessage}: the server applied the predicted walk
   * request with this seq. `main.ts` feeds it to the {@link PredictedWalker},
   * which pops its pending queue head. Off-snapshot per-step movement control
   * (UO-style prediction), so it has its own callback like {@link onSpeech}.
   */
  onMoveAck?: (seq: number) => void;

  /**
   * Fired on every {@link MoveRejectMessage}: an authoritative reposition -
   * a denied walk request (`seq > 0`) or a server-initiated snap such as a
   * teleporter landing (`seq === 0`). `main.ts` feeds it to the
   * {@link PredictedWalker}, which clears its queue and snaps to the carried pose.
   */
  onMoveReject?: (msg: MoveRejectMessage) => void;

  constructor(private readonly send: Send) {}

  setPerformanceMonitor(monitor: ClientPerformanceMonitor | undefined): void {
    this.performanceMonitor = monitor;
  }

  /**
   * Encode and send a client->server message. Every message the client sends is a
   * JSON type, so {@link encode} always returns a `string` here (the binary codec is
   * server->client only, ADR-0190); the cast narrows its widened `string | Uint8Array`
   * return so the one string-only {@link Send} sink stays correct.
   */
  private sendJson(msg: ClientMessage): void {
    this.send(encode(msg) as string);
  }

  /**
   * Send the first {@link LoginMessage} that carries this connection's chosen
   * identity (Slice A). Identity no longer rides the connect URL: the socket opens
   * bare and `main.ts` calls this once the socket is open, then the server answers
   * with a `Welcome` (spawned) or a `Rejected` (refused). The cosmetic fields are
   * exactly what the splash collected.
   */
  login(fields: Omit<LoginMessage, 'kind'>): void {
    this.sendJson({ kind: 'Login', ...fields });
  }

  /**
   * Open a **read-only spectator** view: ask the server to stream the live world to
   * this connection without spawning a player (the login-screen backdrop). No
   * `Welcome` follows - the server sends a keyframe and then per-tick deltas, which
   * arrive through {@link receive} exactly like any other snapshot stream, so
   * {@link onSnapshot} fires and `main.ts` can build the renderer from the first
   * world. A spectator owns no entity, so {@link sendCommand}/{@link say} stay inert.
   */
  spectate(): void {
    this.sendJson({ kind: 'Spectate' });
  }

  /** The latest read-model world, or `undefined` before the first message. */
  get world(): World | undefined {
    return this.replica.current;
  }

  /** The local player's server-assigned EntityId, or `undefined` pre-Welcome. */
  get playerId(): EntityId | undefined {
    return this.ownedEntity;
  }

  /** The server-assigned client id, or `undefined` pre-Welcome. */
  get clientId(): ClientId | undefined {
    return this.assignedClientId;
  }

  /**
   * Consume one raw inbound wire payload from the server - a `string` for the JSON
   * message types, or binary (`ArrayBuffer`/`Uint8Array`, the browser's
   * `binaryType: 'arraybuffer'`) for the three hot world messages the positional codec
   * owns (ADR-0190). {@link decode} routes each by type. A `Welcome` teaches the
   * client its identity and seeds the replica; a full `Snapshot` (the connect-time
   * map-bearing seed) replaces it wholesale; a periodic map-less `Keyframe` (issue
   * #119) resyncs the entity set wholesale while carrying the retained map forward; a
   * `Delta` mutates the replica **incrementally** (ADR-0324). A malformed payload, or
   * a keyframe/delta arriving before any baseline (which the server's connect-time
   * map-bearing frame makes impossible), is ignored rather than allowed to throw, so
   * a single bad frame can never wedge the render loop.
   */
  receive(data: string | ArrayBuffer | Uint8Array): void {
    let message;
    try {
      message = decode(data);
    } catch {
      return;
    }
    if (
      message.kind !== 'Welcome' &&
      message.kind !== 'Snapshot' &&
      message.kind !== 'Keyframe' &&
      message.kind !== 'Delta'
    ) {
      // One wire frame, one invalidation - a batch of cues (PRD #825) is still
      // a single frame's worth of presentation change, so the renderer is woken
      // once for the whole burst rather than once per cue inside it.
      this.onPresentationInvalidated?.(true);
    }
    this.dispatch(message);
  }

  /**
   * Route one **decoded** protocol message to its handler. Split out of
   * {@link receive} so a {@link CueBatchMessage} can hand each of its staged
   * cues through the very same switch, in order, exactly as if the server had
   * sent them as separate frames (PRD #825) - the envelope carries no semantics
   * of its own and no handler below can tell the difference.
   */
  private dispatch(message: ProtocolMessage): void {
    switch (message.kind) {
      case 'CueBatch': {
        for (const cue of message.cues) this.dispatch(cue);
        return;
      }
      case 'Welcome': {
        this.assignedClientId = message.clientId;
        this.ownedEntity = message.entityId;
        // Compare build ids first, *before* touching the snapshot (Issue #320): a
        // compatibility-breaking deploy may make the snapshot un-deserializable, and
        // we still want the stale client routed into the reload flow. onWelcome fires
        // on every reconnect, so this checks after each redeploy blip with no polling.
        this.onServerBuild?.(message.buildId, message.forceReload ?? false);
        this.onCardsEnabled?.(message.cardsEnabled === true);
        // Reveal-only admin signal (ADR-0183): fires before the snapshot is adopted,
        // like onServerBuild, so the shell can (un)wire admin UI on every reconnect.
        this.onIsAdmin?.(message.isAdmin === true);
        if (message.profile) this.onProfile?.(message.profile);
        const world = this.tryAdoptSnapshot(message.snapshot);
        if (world === undefined) return;
        this.keyframePresentationSignature = JSON.stringify(message.snapshot.entities);
        this.onWelcome?.(message.entityId, world);
        this.onSnapshot?.(world);
        return;
      }
      case 'Snapshot': {
        // A full, map-bearing keyframe (the connect-time seed): construct/replace
        // the replica wholesale (the authoritative recovery path, ADR-0324).
        const world = this.tryAdoptSnapshot(message);
        if (world === undefined) return;
        this.keyframePresentationSignature = JSON.stringify(message.entities);
        this.onSnapshot?.(world);
        this.onPresentationInvalidated?.(true);
        return;
      }
      case 'Keyframe': {
        // A periodic map-less keyframe (issue #119): a full entity-set resync that
        // carries the static map forward from the replica's retained baseline map.
        // It replaces the replica wholesale (authoritative and self-correcting),
        // discarding any stale incremental state (ADR-0324). One arriving before
        // any baseline (which the server's connect-time map-bearing frame makes
        // impossible) is dropped as a safe no-op inside the replica.
        const signature = JSON.stringify(message.entities);
        const presentationChanged = signature !== this.keyframePresentationSignature;
        let world: World | undefined;
        try {
          world = this.performanceMonitor
            ? this.performanceMonitor.measure('replica-application', () =>
                this.replica.applyKeyframe(message),
              )
            : this.replica.applyKeyframe(message);
        } catch (error) {
          this.onVersionSkew?.(error);
          return;
        }
        if (world === undefined) return;
        this.keyframePresentationSignature = signature;
        this.onSnapshot?.(world);
        if (presentationChanged) this.onPresentationInvalidated?.(true);
        return;
      }
      case 'Delta': {
        // An ordinary field-level delta: applied **incrementally** to the replica
        // (ADR-0324) - only the added/removed/cleared/changed state is touched, and
        // unchanged entities/components keep object identity. A delta with no
        // baseline yet, or a malformed frame (the replica discards its partially-
        // mutated world and waits for the keyframe resync), is a safe no-op.
        const presentationChanged =
          message.removed.length > 0 ||
          Object.keys(message.added).length > 0 ||
          Object.keys(message.changed).length > 0;
        const world = this.performanceMonitor
          ? this.performanceMonitor.measure('replica-application', () =>
              this.replica.applyDelta(message),
            )
          : this.replica.applyDelta(message);
        if (world === undefined) return;
        this.onSnapshot?.(world);
        if (presentationChanged) this.onPresentationInvalidated?.(true);
        return;
      }
      case 'Speech': {
        // An ephemeral spoken line, delivered off the snapshot stream. Surface it
        // straight to the presentation layer; it never touches the read-model.
        this.onSpeech?.(message);
        return;
      }
      case 'ChatHistoryRemove': {
        this.onChatHistoryRemove?.(message);
        return;
      }
      case 'ActionAnimation': {
        // A one-shot clip broadcast (ADR-0116), delivered off the snapshot stream
        // exactly like Speech. Surface it straight to the presentation layer; it
        // never touches the read-model (an animation moment is not world state).
        this.onActionAnimation?.(message);
        return;
      }
      case 'StormStatus': {
        // The live Blackthorn's Storm board (ADR-0151). Ephemeral event state off the
        // snapshot stream, handed straight to Slice F's overlay; never the read-model world.
        this.onStormStatus?.(message);
        return;
      }
      case 'StormTelegraph': {
        // A one-shot vortex-summon telegraph (ADR-0151). Ephemeral like ActionAnimation;
        // handed to Slice E's danger-marker layer, never the read-model world.
        this.onStormTelegraph?.(message);
        return;
      }
      case 'JusticeFx': {
        // A one-shot King's Justice smoke poof (ADR-0194). Ephemeral like a telegraph;
        // handed to the poof FX/sound handler, never the read-model world.
        this.onJusticeFx?.(message);
        return;
      }
      case 'BloodFx': {
        // A one-shot server-rolled blood scatter (PRD #372). Ephemeral like a telegraph;
        // handed to the BloodLayer splat primitive, never the read-model world.
        this.onBloodFx?.(message);
        return;
      }
      case 'ClearCorpses': {
        // A one-shot "wipe every fallen body" signal (ADR-0140), fired at storm end and from
        // the admin dashboard. Ephemeral like a telegraph; never touches the read-model world.
        this.onClearCorpses?.();
        return;
      }
      case 'RatSwarmStatus': {
        // The live event board (ADR-0119), delivered off the snapshot stream
        // like Speech: ephemeral event state, never the read-model world.
        this.onRatSwarmStatus?.(message);
        return;
      }
      case 'GoldUpdate': {
        // An owner-only gold anchor (ADR-0119), the OnlineTime pattern: hand it
        // straight to the HUD as fresh server truth, off the read-model world.
        this.onGoldUpdate?.(message.gold, message.durable);
        return;
      }
      case 'DuelPrompt': {
        // An owner-only duel challenge prompt (ADR-0130), the ephemeral-event
        // pattern: hand it to the duel UI to open/dismiss the accept/decline
        // window; never touches the read-model world.
        this.onDuelPrompt?.(message);
        return;
      }
      case 'DuelCombat': {
        // An owner-only "here is your duel opponent" signal (ADR-0255), the same
        // ephemeral-event pattern: hand the opponent id to the fire-bow prompt
        // driver; never touches the read-model world.
        this.onDuelCombat?.(message);
        return;
      }
      case 'SpherePrompt': {
        this.onSpherePrompt?.(message);
        return;
      }
      case 'SphereState': {
        this.onSphereState?.(message);
        return;
      }
      case 'SphereEvent': {
        this.onSphereEvent?.(message);
        return;
      }
      case 'SphereCommandResult': {
        this.onSphereCommandResult?.(message);
        return;
      }
      case 'DiceWagerPrompt': {
        // An owner-only dice-wager challenge prompt (ADR-0154), the same ephemeral-event
        // pattern as DuelPrompt: hand it to the duel UI to open/dismiss the accept/decline
        // window (with dice copy); never touches the read-model world.
        this.onDiceWagerPrompt?.(message);
        return;
      }
      case 'CardDuelPrompt': {
        // An owner-only card-duel challenge prompt (ADR-0286), the same ephemeral-event
        // pattern as DuelPrompt: hand it to the duel UI to open/dismiss the accept/decline
        // window (with card-duel copy); never touches the read-model world.
        this.onCardDuelPrompt?.(message);
        return;
      }
      case 'CardDuelListings': {
        this.onCardDuelListings?.(message);
        return;
      }
      case 'CardDuelListingAccepted': {
        this.onCardDuelListingAccepted?.(message);
        return;
      }
      case 'CardDuelState': {
        // The owner-only, per-seat directed card-duel battle state (ADR-0286): hand it to
        // the battle gump to render/close; never touches the read-model world.
        this.onCardDuelState?.(message);
        return;
      }
      case 'CombatText': {
        // A floating combat number (ADR-0131), the ephemeral-event pattern: hand
        // it to the floating-combat-text layer; never touches the read-model world.
        this.onCombatText?.(message);
        return;
      }
      case 'DungeonRunStatus': {
        this.onDungeonRunStatus?.(message);
        return;
      }
      case 'DungeonTelegraph': {
        // One announced area (PRD #793). Ephemeral like a storm telegraph:
        // handed to the ground-cue layer, never the read-model world.
        this.onDungeonTelegraph?.(message);
        return;
      }
      case 'DungeonCastResult': {
        this.onDungeonCastResult?.(message);
        return;
      }
      case 'SoulstoneRoster': {
        this.onSoulstoneRoster?.(message);
        return;
      }
      case 'Rejected': {
        // The login was refused (Slice A: a reserved name in use by another
        // connection). No entity exists for us; surface the reason so the splash
        // can let the visitor edit the name and retry. The socket closes next.
        this.onRejected?.(message.reason);
        return;
      }
      case 'CharacterRequired': {
        this.onCharacterRequired?.();
        return;
      }
      case 'OnlineTime': {
        // An owner-only online-time anchor (Slice D), delivered off the snapshot
        // stream. Hand it straight to the HUD as a fresh server truth; it never
        // touches the read-model world.
        this.onOnlineTime?.(message.totalOnlineMs);
        return;
      }
      case 'Collection': {
        // The earned cosmetic collection (Slice E): account state delivered off the
        // snapshot stream, surfaced straight to the wardrobe panel. It never touches
        // the read-model world (what a player *may* wear, not the world's state).
        this.onCollection?.(message.unlocked);
        return;
      }
      case 'WardrobeFavoritesState': {
        this.onWardrobeFavoritesState?.(message);
        return;
      }
      case 'CardState': {
        // The Card Binder state (PRD #493 Slice 3): owner-only account state
        // (collection / decks / pack count) delivered off the snapshot stream in
        // reply to OpenBinder / SaveCardDecks. Never touches the read-model world.
        this.onCardState?.(message);
        return;
      }
      case 'PackOpened': {
        // The five cards a just-opened pack revealed (PRD #493 Slice 4): owner-only,
        // pushed just before the CardState that banks them, for the flip-reveal
        // modal. Presentation only - the collection lands via the CardState.
        this.onPackOpened?.(message.cards, message.duplicateGold);
        return;
      }
      case 'AutoEquipState': {
        // The persisted auto-equip preference (ADR-0123): owner-only account state
        // delivered off the snapshot stream at login, surfaced to the wardrobe
        // checkbox so it opens reflecting the saved choice. Never world state.
        this.onAutoEquipState?.(message.enabled);
        return;
      }
      case 'ProfileState': {
        this.onProfile?.(message.profile);
        return;
      }
      case 'AchievementAward': {
        this.onAchievementAward?.(message.achievement);
        return;
      }
      case 'MoveAck': {
        // Per-step prediction control (off-snapshot): the server applied this seq.
        this.onMoveAck?.(message.seq);
        return;
      }
      case 'MoveReject': {
        // An authoritative reposition: denied request, or a seq-0 server snap.
        this.onMoveReject?.(message);
        return;
      }
      case 'ArenaRunStatus': {
        // The owner-only Arena run board (Slice D/E): ephemeral event state off the
        // snapshot stream, like the storm board. Never the read-model world.
        this.onArenaRunStatus?.(message);
        return;
      }
      case 'ArenaDraft': {
        // The owner-only pending level-up draft (Slice D/E): the run holds paused
        // until a `PickArenaOption` answers it. Ephemeral, never world state.
        this.onArenaDraft?.(message);
        return;
      }
      case 'ArenaResults': {
        // The one owner-only results push of a finished run (Slice D/E). Ephemeral.
        this.onArenaResults?.(message);
        return;
      }
      case 'ArenaRecord': {
        // A broadcast new-Arena-high-score celebration (ADR-0210). Ephemeral: a
        // dropped frame only misses one victory sting.
        this.onArenaRecord?.(message);
        return;
      }
      case 'ArenaFx': {
        // One skill volley's FX cascade (fire-mage rework, ADR-0179). Ephemeral:
        // a dropped message only misses the show - damage is already applied.
        this.onArenaFx?.(message);
        return;
      }
      case 'ArenaWarning': {
        // An elite/boss entrance FX trigger, keyed to the sim's spawn beat (not a
        // body-id guess). Ephemeral: a dropped frame only misses one sting.
        this.onArenaWarning?.(message);
        return;
      }
      case 'ArenaHistoryPage': {
        this.onArenaHistoryPage?.(message);
        return;
      }
      // Login/ClientCommand/Walk/Say are client->server only.
    }
  }

  /**
   * Adopt a full map-bearing snapshot into the replica, but **never let a
   * deserialize throw escape** (Issue #320). The load-bearing case is a
   * `SCHEMA_VERSION` skew after a breaking deploy: the stale client's
   * `deserialize` throws `Incompatible save version`, which used to propagate out
   * of the socket `message` listener and strand the player. Here it is caught and
   * routed to {@link onVersionSkew} (which degrades to the reload flow); the
   * replica constructs its world before adopting anything, so a failed adoption
   * leaves the previous read-model intact and a later good frame can still
   * resync. Returns the adopted world, or `undefined` on failure so callers skip
   * their post-adopt callbacks.
   */
  private tryAdoptSnapshot(snapshot: WorldSnapshotMessage): World | undefined {
    try {
      return this.performanceMonitor
        ? this.performanceMonitor.measure('replica-application', () =>
            this.replica.adoptSnapshot(snapshot),
          )
        : this.replica.adoptSnapshot(snapshot);
    } catch (error) {
      this.onVersionSkew?.(error);
      return undefined;
    }
  }

  /**
   * Express player intent: wrap an *unchanged* sim {@link Command} as a
   * {@link ClientCommandMessage} tagged with this client's id and send it up the
   * socket. Until the Welcome assigns an id there is nothing to attribute the
   * command to, so it is dropped rather than sent unattributed (the server would
   * reject it anyway). This is the only way intent leaves the browser - there is
   * no local enqueue, the command queue lives on the server.
   */
  sendCommand(command: Command): void {
    if (this.assignedClientId === undefined) return;
    this.sendJson({ kind: 'ClientCommand', clientId: this.assignedClientId, command });
  }

  requestArenaHistory(entity: EntityId, before?: number, limit = 20): void {
    if (this.assignedClientId === undefined) return;
    this.sendJson({ kind: 'ArenaHistoryRequest', entity, ...(before !== undefined ? { before } : {}), limit });
  }

  /**
   * Send one predicted walk request (a turn or step the local player already
   * performed under prediction) up the socket. Emitted by the
   * {@link PredictedWalker} at each action start; like {@link sendCommand} it
   * waits for the Welcome, since pre-spawn there is no entity to move.
   */
  sendWalk(msg: WalkRequestMessage): void {
    if (this.assignedClientId === undefined) return;
    this.sendJson(msg);
  }

  /** Send one dungeon-only continuous movement intent change. */
  sendDungeonMoveIntent(msg: DungeonMoveIntentMessage): void {
    if (this.assignedClientId === undefined) return;
    this.sendJson(msg);
  }

  /**
   * Speak a line: send a {@link SayMessage} carrying the text and any attached
   * concrete TCG card references up the socket. The sender is **not** attached -
   * the server stamps the speaker
   * from this connection's owned entity, so there is nothing to spoof. An empty
   * or whitespace-only line is dropped client-side; the server caps the length.
   * Like {@link sendCommand} it waits for the Welcome so the connection has an
   * owned entity for the server to attribute the line to.
   */
  say(text: string, cards?: WireCardLink[]): void {
    if (this.assignedClientId === undefined) return;
    const line = text.trim();
    if (line.length === 0) return;
    this.sendJson({ kind: 'Say', text: line, ...(cards && cards.length > 0 ? { cards } : {}) });
  }

  /**
   * Choose the hue this player's own speech text renders in (overhead and in the
   * journal). Unlike {@link say}, this is a per-connection cosmetic preference the
   * server keeps on the session and stamps onto every later line, so it is sent
   * once when it changes (and re-sent after a Welcome to restore a saved choice).
   * `0` selects the plain-white hue-0 default. Waits for the Welcome like the
   * other senders, since the server has no session to attach it to until then.
   */
  setSpeechHue(hue: number): void {
    if (this.assignedClientId === undefined) return;
    this.sendJson({ kind: 'SetSpeechHue', hue });
  }

  /**
   * Dress the character from its collection (Cosmetic Progression Slice E,
   * ADR-0050): send the whole desired worn `outfit` (a flat `{item, hue}` list; an
   * empty list bares the body) up the socket as an {@link EquipMessage}. The server
   * validates every pair against what the account owns, assembles + persists it, and
   * the new look comes back in the next snapshot - so, like {@link sendCommand}, this
   * is intent only, never a local mutation, and it waits for the Welcome so there is
   * an owned entity to dress.
   */
  equip(outfit: WardrobePair[]): void {
    if (this.assignedClientId === undefined) return;
    this.sendJson({ kind: 'Equip', outfit });
  }

  unlockWardrobeFavorite(): void {
    if (this.assignedClientId === undefined) return;
    this.sendJson({ kind: 'UnlockWardrobeFavorite' });
  }

  saveWardrobeFavorite(slot: number, outfit: WardrobePair[]): void {
    if (this.assignedClientId === undefined) return;
    this.sendJson({ kind: 'SaveWardrobeFavorite', slot, outfit });
  }

  /**
   * Set the player's **auto-equip preference** (auto-equip toggle, ADR-0123): send a
   * {@link import('./protocol').SetAutoEquipMessage} carrying the wardrobe checkbox's
   * new state up the socket. Like {@link setSpeechHue} this is a per-connection
   * preference the server persists (per reserved account) and honours at every later
   * unlock boundary, so it is intent only - the effect (auto-dress or not) comes back
   * in the ordinary snapshot when the next unlock lands. Waits for the Welcome, since
   * the server has no session to attach it to until then.
   */
  setAutoEquip(enabled: boolean): void {
    if (this.assignedClientId === undefined) return;
    this.sendJson({ kind: 'SetAutoEquip', enabled });
  }

  /**
   * Signal that the player **opened the Card Binder** (trading card game, PRD #493
   * Slice 3). Sends an {@link import('./protocol').OpenBinderMessage}; the server
   * performs the one-time starter grant (reserved names only, idempotent) and replies
   * with a {@link CardStateMessage} the Binder renders. Intent only - the whole Binder
   * state comes back off the wire. Waits for the Welcome, since the server has no
   * session/account to read until then.
   */
  openBinder(): void {
    if (this.assignedClientId === undefined) return;
    this.sendJson({ kind: 'OpenBinder' });
  }

  requestSoulstones(): void {
    if (this.assignedClientId === undefined) return;
    this.sendJson({ kind: 'RequestSoulstones' });
  }

  attuneSoulstone(classId: string): void {
    if (this.assignedClientId === undefined) return;
    this.sendJson({ kind: 'AttuneSoulstone', classId });
  }

  equipHeroItem(soulstoneId: string, itemId: string): void {
    if (this.assignedClientId === undefined) return;
    this.sendJson({ kind: 'EquipHeroItem', soulstoneId, itemId });
  }

  /** Take one slot's item off an owned Soulstone's Hero (PRD #795). */
  unequipHeroItem(soulstoneId: string, slot: string): void {
    if (this.assignedClientId === undefined) return;
    this.sendJson({ kind: 'UnequipHeroItem', soulstoneId, slot });
  }

  /**
   * Spend attribute points on an owned Soulstone's Hero (PRD #792). Sends the
   * whole desired allocation rather than a delta, and the server decides
   * whether it is one the Hero's level could have earned.
   */
  allocateHeroStats(soulstoneId: string, stats: HeroStats): void {
    if (this.assignedClientId === undefined) return;
    this.sendJson({ kind: 'AllocateHeroStats', soulstoneId, stats });
  }

  /** Refund every allocated point on an owned Soulstone's Hero for the declared gold price. */
  respecHero(soulstoneId: string): void {
    if (this.assignedClientId === undefined) return;
    this.sendJson({ kind: 'RespecHero', soulstoneId });
  }

  /**
   * Take up one owned Soulstone inside the Bone Knight Wall (PRD #837
   * slice 3), optionally at one of its unlocked difficulty tiers (slice 7).
   */
  takeUpSoulstone(soulstoneId: string, tier?: string): void {
    if (this.assignedClientId === undefined) return;
    this.sendJson({
      kind: 'TakeUpSoulstone',
      soulstoneId,
      ...(tier !== undefined ? { tier } : {}),
    });
  }

  /**
   * Wipe one owned Soulstone (PRD #837 slice 3). Destroys durable player data,
   * so the dialog confirms explicitly before this is ever called.
   */
  wipeSoulstone(soulstoneId: string): void {
    if (this.assignedClientId === undefined) return;
    this.sendJson({ kind: 'WipeSoulstone', soulstoneId });
  }

  chooseStarterRecipe(recipeId: StarterRecipeId): void {
    if (this.assignedClientId === undefined) return;
    this.sendJson({ kind: 'ChooseStarterRecipe', recipeId });
  }

  /**
   * **Save the player's named decks** (trading card game, PRD #493 Slice 3). Sends a
   * {@link import('./protocol').SaveCardDecksMessage} carrying the whole desired set
   * (the deck-builder validated them client-side first). The server re-validates
   * (reserved, <= 3, each a legal owned 20-card deck) and echoes the authoritative
   * {@link CardStateMessage} back, so a rejected/clamped save reverts the client.
   * Intent only; waits for the Welcome like the other senders.
   */
  saveCardDecks(decks: WireNamedDeck[]): void {
    if (this.assignedClientId === undefined) return;
    this.sendJson({ kind: 'SaveCardDecks', decks });
  }

  selectTitle(titleId: string | null): void {
    if (this.ownedEntity === undefined) return;
    this.sendJson({ kind: 'SelectTitle', entity: this.ownedEntity, titleId });
  }

  /**
   * Toggle admin **no-clip** (walk through blocked tiles), ADR-0183. Sends an
   * {@link import('./protocol').AdminSetNoClipMessage}; the server honours it **only**
   * if this connection is a passcode-proven admin (else a logged no-op), so this is
   * intent, never local authority. Waits for the Welcome, like the other senders.
   * The client mirrors the flag for movement prediction, but the server stays the sole
   * authority - a lie here is corrected at the wall by the server's reject/snap.
   */
  setAdminNoClip(enabled: boolean): void {
    if (this.assignedClientId === undefined) return;
    this.sendJson({ kind: 'AdminSetNoClip', enabled });
  }

  /**
   * Admin-**teleport** the player's own character to a tile (ADR-0183). Sends an
   * {@link import('./protocol').AdminTeleportMessage}; the server re-authorizes (admin
   * only) and validates the coords (integer, in-bounds) before applying it off-queue
   * and snapping this client with a seq-0 `MoveReject`. Intent only, like the others;
   * waits for the Welcome so there is an owned entity to move.
   */
  adminTeleport(x: number, y: number): void {
    if (this.assignedClientId === undefined) return;
    this.sendJson({ kind: 'AdminTeleport', x, y });
  }

  /** Request a grow, shrink, or immediate normal-size action from the admin seam. */
  setAdminSize(size: AdminSize): void {
    if (this.assignedClientId === undefined) return;
    this.sendJson({ kind: 'AdminSetSize', size });
  }

  /** Ask the server to equip the admin-only 2x bascinet on this character. */
  adminEquipBascinet(): void {
    if (this.assignedClientId === undefined) return;
    this.sendJson({ kind: 'AdminEquipBascinet' });
  }

  /**
   * Ask the server to put this character on a mount, or take it off one (ADR-0442).
   * Sending the body already ridden dismounts, so the admin panel's single button
   * toggles. Intent only: the server re-authorizes, validates the body against the
   * registered mounts, and owns the replicated result.
   */
  adminSetMount(body?: number): void {
    if (this.assignedClientId === undefined) return;
    this.sendJson({ kind: 'AdminSetMount', body });
  }
}
